import apiInstance from '@services/api/apiInstance'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import {
  accessTokenAtom,
  refreshTokenAtom,
  userAtom,
  DecodedToken,
  userRolesAtom
} from '@services/token/token.models'
import {
  storeTokens,
  clearStoredTokens,
  hasStoredTokens,
  TOKEN_STORAGE_EVENT,
  StoredTokens
} from '@services/token/tokenStorage'
import { useEffect } from 'react'
import { clarity } from 'react-microsoft-clarity'

// Function for decoding JWT token
export const decodeJWT = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding JWT token:', error)
    return null
  }
}

// Hook for handling user roles
export const useUserRoles = () => {
  const [accessToken] = useAtom(accessTokenAtom)
  const [roles, setRoles] = useAtom(userRolesAtom)

  useEffect(() => {
    // First check for Microsoft OAuth user_roles in localStorage
    const msUserRoles = localStorage.getItem('user_roles')
    if (msUserRoles && msUserRoles !== '[]' && msUserRoles !== 'undefined') {
      try {
        const parsedRoles = JSON.parse(msUserRoles)
        if (Array.isArray(parsedRoles) && parsedRoles.length > 0) {
          console.log('Using MS OAuth roles:', parsedRoles)
          setRoles(parsedRoles)
          return
        }
      } catch {
        // Continue to JWT decoding
      }
    }

    // Fall back to JWT token decoding
    if (accessToken) {
      const decodedToken = decodeJWT(accessToken)
      if (decodedToken && decodedToken.roles) {
        setRoles(decodedToken.roles)
      } else {
        setRoles([])
      }
    } else {
      setRoles([])
    }
  }, [accessToken, setRoles])

  const hasRole = (role: string): boolean => {
    return roles.includes(role)
  }

  const isTechnician = (): boolean => {
    // Tylko technicy powinni mieć dostęp do helpdesku
    // Administratorzy i staff to NIE technicy
    const result = hasRole('technician') || hasRole('technicians')
    console.log('isTechnician check:', { roles, result })
    return result
  }

  const isAdministrator = (): boolean => {
    return hasRole('administrator') || hasRole('administrators') || hasRole('sys_admin')
  }

  const isStaff = (): boolean => {
    return hasRole('staff')
  }

  return {
    roles,
    hasRole,
    isTechnician,
    isAdministrator,
    isStaff
  }
}

interface UseObtainParams {
  onError: () => void
}

export const useObtain = ({ onError }: UseObtainParams) => {
  const setAccessToken = useSetAtom(accessTokenAtom)
  const setRefreshToken = useSetAtom(refreshTokenAtom)
  const setUser = useSetAtom(userAtom)

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiInstance<{ access: string; refresh: string }>({
        url: '/admin/token/obtain/',
        method: 'POST',
        data: {
          email,
          password
        }
      }).then((response) => {
        // Zapisz użytkownika do localStorage
        localStorage.setItem('user', JSON.stringify(email))
        setUser(email)
        if (clarity.hasStarted()) {
          clarity.identify(email, { email })
          clarity.start()
        }
        return response
      }),
    onSuccess: ({ data }) => {
      // Zapisz spójnie we wszystkich formatach localStorage
      storeTokens({ access: data.access, refresh: data.refresh })

      // Ustaw atomy
      setAccessToken(data.access)
      setRefreshToken(data.refresh)
    },
    onError
  })
}

export const useAccessToken = () => {
  const queryClient = useQueryClient()
  const [accessToken, setAccessToken] = useAtom(accessTokenAtom)
  const [refreshToken, setRefreshToken] = useAtom(refreshTokenAtom)
  const setUser = useSetAtom(userAtom)

  const hasLocalStorageTokens = hasStoredTokens()

  const clearUserData = async () => {
    if (clarity.hasStarted()) {
      clarity.stop()
    }
    // Wyczyść wszystkie tokeny z localStorage (oba formaty)
    clearStoredTokens()

    // Wyczyść atomy
    setAccessToken(null)
    setRefreshToken(null)
    setUser(null)

    // Wyczyść cache zapytań
    queryClient.removeQueries()

    // Przekieruj do strony logowania
    window.location.href = '/'
  }

  return { accessToken, refreshToken, clearUserData, hasLocalStorageTokens }
}

export const useRefreshToken = () => {
  const setAccessToken = useSetAtom(accessTokenAtom)
  const [refreshToken, setRefreshToken] = useAtom(refreshTokenAtom)

  // Keep atoms in sync when tokens are refreshed outside of React (e.g. by the
  // axios response interceptor on a 401). Without this the atoms would keep a
  // stale refresh token and the proactive refresh/verify calls would fail.
  useEffect(() => {
    const handleTokensUpdated = (event: Event) => {
      const detail = (event as CustomEvent<StoredTokens>).detail
      if (!detail) return
      setAccessToken(detail.access)
      setRefreshToken(detail.refresh)
    }

    window.addEventListener(TOKEN_STORAGE_EVENT, handleTokensUpdated)
    return () => window.removeEventListener(TOKEN_STORAGE_EVENT, handleTokensUpdated)
  }, [setAccessToken, setRefreshToken])

  const { isError, error } = useQuery({
    queryKey: ['refreshToken '],
    staleTime: 0,
    refetchInterval: 1200000, //20min - refresh 10min before token expires
    refetchIntervalInBackground: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      apiInstance<{
        access: string
        refresh: string
      }>({
        url: '/admin/token/refresh/',
        method: 'POST',
        data: {
          refresh: refreshToken
        }
      }).then(({ data }) => {
        console.log('🔄 Token refreshed successfully at:', new Date().toISOString())

        // Zapisz spójnie we wszystkich formatach localStorage (klasyczny + MS/AD)
        storeTokens({ access: data.access, refresh: data.refresh })

        // Ustaw atomy
        setAccessToken(data.access)
        setRefreshToken(data.refresh)
        return data
      }),

    enabled: !!refreshToken
  })

  useEffect(() => {
    if (isError) {
      console.log(
        '⚠️ useRefreshToken error occurred:',
        error,
        'but not logging out (interceptor handles this)'
      )
      // Don't logout here - let the interceptor handle token refresh failures
      // The interceptor has better logic and retry mechanisms
    }
  }, [isError, error])

  return {}
}

export const useVerifyToken = () => {
  const queryClient = useQueryClient()
  const [accessToken, setAccessToken] = useAtom(accessTokenAtom)
  const setRefreshToken = useSetAtom(refreshTokenAtom)
  const setUser = useSetAtom(userAtom)

  const { data, isError, error, ...rest } = useQuery({
    queryKey: ['verifyToken'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 1800000, //30min - verify token every 30min
    staleTime: 0,
    queryFn: () =>
      apiInstance<{
        detail: string
        code: string
      }>({
        url: '/admin/token/verify/',
        method: 'POST',
        data: {
          token: accessToken
        }
      }).then(({ data }) => data),
    enabled: !!accessToken
  })

  useEffect(() => {
    if (isError && (error as any)?.code !== 'ERR_NETWORK') {
      if (clarity.hasStarted()) {
        clarity.stop()
      }
      clearStoredTokens()
      setAccessToken(null)
      setRefreshToken(null)
      setUser(null)
      queryClient.removeQueries()
    }
  }, [isError, queryClient, setAccessToken, setRefreshToken])

  return {
    data,
    ...rest
  }
}

export const useUserPasswordReset = () => {
  return useMutation({
    mutationFn: ({ password, token }: { password: string; token: string }) =>
      apiInstance<any>({
        url: '/admin/password/reset/',
        method: 'POST',
        data: { password, resetKey: token }
      })
  })
}
